// THE LOWEST ACTIVITY, WITH WHERE IT SITS UNDER IT.
//
// Owner, 2026-10-15: "force all activities selection, to show only the
// lowest child, in any leaf, but below each leaf's child, it shows full path,
// so users can understand where that child activity is at, do the same for
// issue creation, shopdrawing requests".
//
// Every ASSIGNMENT picker (an issue's activity, a shop drawing's activity,
// the bulk forms that set one) offers only LEAVES - rows nothing else hangs
// under - because work filed on "Level 1" or "Coordination" lands on a
// summary row no one owns. Each option reads as two lines: the activity's own
// name, and under it the path above it in smaller type. The closed field shows
// the name (optionLabelProp="short"); typing searches name AND path
// (optionFilterProp="search"), so "Area E busway" finds it from either end.
//
// FILTERS ARE NOT THIS. A filter may pick any level and takes everything
// below it (utils/activitySubtree on the server) - see ActivityFilterSelect.
//
// A value already on a record that is not a leaf any more (someone added rows
// under it) is still SHOWN, disabled, so the field reads as words rather than
// an id and the person is asked to choose a lower row.
import React from "react";

export const PATH_SEP = " › ";
export const NOT_LEAF_NOTE = "has activities under it - pick one of those";

/** ids that have at least one child in `items` (each { _id | id, parentId }). */
export function parentIdsOf(items) {
  const out = new Set();
  for (const it of items || []) {
    if (it && it.parentId && !it.deleted) out.add(String(it.parentId));
  }
  return out;
}

const idOf = (it) => String(it._id || it.id || "");

/** One option: name on top, path beneath. */
export function leafOption({ value, name, path = [], title = "", disabled = false, note = "" }) {
  const pathText = (path || []).filter(Boolean).join(PATH_SEP);
  return {
    value: String(value),
    disabled,
    short: name,
    // Name, path and any note, so a search (and a test) reads the words shown.
    // The path in both spellings people type: "a › b" and the digest's "a / b".
    search: `${pathText}${PATH_SEP}${name}${note ? ` - ${note}` : ""} | ${[...(path || []).filter(Boolean), name].join(" / ")}`,
    title: title || (pathText ? `${pathText}${PATH_SEP}${name}` : name),
    label: (
      <div style={{ lineHeight: 1.25, padding: "2px 0", whiteSpace: "normal" }}>
        <div style={{ fontWeight: 500 }}>{name}{note ? <span style={{ color: "#b54708", fontWeight: 400 }}> - {note}</span> : null}</div>
        {pathText ? <div style={{ fontSize: 11, color: "#667085" }}>{pathText}</div> : null}
      </div>
    ),
  };
}

/**
 * Schedule rows (already through withActivityPaths, so each has `path`) ->
 * leaf options. `all` is the WHOLE schedule, used to tell a leaf from a
 * parent (a row filtered out for being done is still somebody's parent);
 * `offer(item)` decides which leaves are offered (default: every row).
 */
export function leafActivityOptions(all, { offer = () => true, currentId = null, suffix = null } = {}) {
  const list = Array.isArray(all) ? all : [];
  const parents = parentIdsOf(list);
  const opts = [];
  for (const it of list) {
    if (!it || it.deleted || parents.has(idOf(it)) || !offer(it)) continue;
    opts.push(leafOption({ value: idOf(it), name: it.name || "(unnamed)", path: it.path || [], note: suffix ? suffix(it) : "" }));
  }
  const cur = currentId == null || currentId === "" ? "" : String(currentId);
  if (cur && !opts.some((o) => o.value === cur)) {
    const it = list.find((x) => idOf(x) === cur);
    if (it) opts.unshift(leafOption({ value: cur, name: it.name || "(unnamed)", path: it.path || [], disabled: true, note: parents.has(cur) ? NOT_LEAF_NOTE : "not offered" }));
  }
  return opts;
}

/** The Select props every leaf picker shares. */
export const LEAF_SELECT_PROPS = {
  showSearch: true,
  optionFilterProp: "search",
  optionLabelProp: "short",
  listHeight: 360,
};
